import { useEffect, useState, useCallback } from "react";
import { Check, X, Save, ImageIcon, Send, Plus, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Panel, PageTitle, StarDivider } from "@/components/common";
import { toast } from "sonner";

const STATUS_STYLE = {
  PENDING_APPROVAL: "bg-[#F59E0B]/20 text-[#F59E0B]",
  APPROVED: "bg-[#10B981]/20 text-[#10B981]",
  MODIFIED: "bg-[#22d3ee]/20 text-[#22d3ee]",
  REJECTED: "bg-[var(--destructive)]/20 text-[var(--destructive)]",
};
const STATUS_LABEL = {
  PENDING_APPROVAL: "In Attesa", APPROVED: "Approvato", MODIFIED: "Modificato", REJECTED: "Invalidato",
};

export default function Risultati() {
  const [tours, setTours] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const isArchived = (tour) =>
    String(tour.state ?? tour.status ?? "").toLowerCase() === "archived";
  const visibleTours = tours.filter((tour) => showArchived || !isArchived(tour));
  const [tid, setTid] = useState("");
  const [lobbies, setLobbies] = useState([]);
  const [lobbyId, setLobbyId] = useState("");
  const [results, setResults] = useState([]);
  const [teams, setTeams] = useState([]);
  const [rosterPlayers, setRosterPlayers] = useState({});
  const [adjustments, setAdjustments] = useState([]);
  const [adjForm, setAdjForm] = useState({ team_id: "", value: "", reason: "" });
  const [edits, setEdits] = useState({}); // key match_id|team_id -> {placement, player_kills:{pid:kills}}
  const [newResults, setNewResults] = useState({}); // key match_number|team_id -> {placement, player_kills}

  useEffect(() => { api.get("/tournaments", { params: { templates: false } }).then(({ data }) => { setTours(data);
    const first = data.find((tour) => !isArchived(tour));
    if (first) setTid(first.id); }); }, []);

  const load = useCallback(() => {
    if (!tid) return;
    api.get("/lobbies", { params: { tournament_id: tid } }).then(({ data }) => setLobbies(data));
    api.get("/teams", { params: { tournament_id: tid } }).then(async ({ data }) => {
      setTeams(data);

      const rosters = await Promise.all(
        data.map(async (team) => {
          try {
            const { data: roster } = await api.get(`/teams/${team.id}/roster`);
            return [team.id, roster];
          } catch {
            return [team.id, []];
          }
        })
      );

      setRosterPlayers(Object.fromEntries(rosters));
    });
    api.get("/adjustments", { params: { tournament_id: tid } }).then(({ data }) => setAdjustments(data));
    const params = { tournament_id: tid }; if (lobbyId) params.lobby_id = lobbyId;
    api.get("/results", { params }).then(({ data }) => setResults(data));
  }, [tid, lobbyId]);
  useEffect(() => { load(); }, [load]);

  const addAdjustment = async () => {
    if (!adjForm.team_id || adjForm.value === "") { toast.error("Seleziona squadra e valore"); return; }
    try {
      await api.post("/adjustments", { tournament_id: tid, team_id: adjForm.team_id, value: +adjForm.value, reason: adjForm.reason });
      toast.success("Adjustment applicato");
      setAdjForm({ team_id: "", value: "", reason: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeAdjustment = async (id) => {
    try { await api.delete(`/adjustments/${id}`); toast.success("Rimosso"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const key = (r) => `${r.match_id}|${r.team_id}`;
  const getEdit = (r) => edits[key(r)] || { placement: r.placement, player_kills: Object.fromEntries(r.player_kills.map((p) => [p.player_id, p.kills])) };
  const setKill = (r, pid, v) => { const e = getEdit(r); setEdits({ ...edits, [key(r)]: { ...e, player_kills: { ...e.player_kills, [pid]: +v } } }); };
  const setPlace = (r, v) => { const e = getEdit(r); setEdits({ ...edits, [key(r)]: { ...e, placement: +v } }); };

  const act = async (r, body, msg) => {
    try { await api.put(`/results/${r.match_id}/${r.team_id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const approve = (r) => act(r, { status: "APPROVED", publish: true }, "Risultato approvato");
  const reject = (r) => act(r, { status: "REJECTED" }, "Risultato invalidato");
  const saveEdit = (r) => { const e = getEdit(r); act(r, { placement: e.placement, player_kills: e.player_kills, status: "MODIFIED", publish: true }, "Risultato modificato"); };

  const publishMatch = async (mid) => {
    try { await api.put(`/matches/${mid}/publish`, { publish: true }); toast.success("Match pubblicato"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const selectedTour = tours.find((t) => t.id === tid);
  const numMatches = Math.max(1, Number(selectedTour?.num_matches || 1));

  const visibleTeams = teams.filter((team) =>
    team.lobby_id && (!lobbyId || team.lobby_id === lobbyId)
  );

  const resultBySlot = new Map(
    results.map((result) => [
      `${result.match_number}|${result.team_id}`,
      result,
    ])
  );

  const newKey = (matchNumber, teamId) => `${matchNumber}|${teamId}`;

  const getNewResult = (matchNumber, team) => {
    const k = newKey(matchNumber, team.id);
    if (newResults[k]) return newResults[k];

    return {
      placement: "",
      player_kills: Object.fromEntries(
        (rosterPlayers[team.id] || []).map((player) => [player.id, 0])
      ),
    };
  };

  const setNewPlace = (matchNumber, team, value) => {
    const k = newKey(matchNumber, team.id);
    const current = getNewResult(matchNumber, team);
    setNewResults((prev) => ({
      ...prev,
      [k]: { ...current, placement: value },
    }));
  };

  const setNewKill = (matchNumber, team, playerId, value) => {
    const k = newKey(matchNumber, team.id);
    const current = getNewResult(matchNumber, team);
    setNewResults((prev) => ({
      ...prev,
      [k]: {
        ...current,
        player_kills: {
          ...current.player_kills,
          [playerId]: Number(value),
        },
      },
    }));
  };

  const createResult = async (matchNumber, team) => {
    const k = newKey(matchNumber, team.id);
    const current = getNewResult(matchNumber, team);
    const placement = Number(current.placement);

    if (!Number.isInteger(placement) || placement < 1) {
      toast.error("Inserisci un piazzamento valido");
      return;
    }

    try {
      await api.post("/results/admin", {
        tournament_id: tid,
        team_id: team.id,
        match_number: matchNumber,
        placement,
        player_kills: current.player_kills,
      });

      toast.success("Risultato inserito");

      setNewResults((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });

      load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const pendingCount = results.filter((r) => r.status === "PENDING_APPROVAL").length;

  return (
    <div>
      <PageTitle title="Risultati" subtitle="Approvazione risultati inviati dal Bot"
        action={<div className="flex gap-3 flex-wrap">
          <select value={tid} onChange={(e) => { setTid(e.target.value); setLobbyId(""); }} data-testid="results-tour-select" className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
            {visibleTours.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select>
          <label className="flex items-center gap-2 text-sm text-[var(--silver)] font-rajdhani cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                const checked = e.target.checked;
                setShowArchived(checked);
                if (!checked && isArchived(tours.find((tour) => tour.id === tid) || {})) {
                  setTid(tours.find((tour) => !isArchived(tour))?.id || "");
                  setLobbyId("");
                }
              }}
              className="accent-[#D4AF37]"
            />
            Mostra tornei archiviati
          </label>
          <select value={lobbyId} onChange={(e) => setLobbyId(e.target.value)} data-testid="results-lobby-select" className="bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none">
            <option value="">Tutte le lobby</option>{lobbies.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        </div>} />

      {pendingCount > 0 && <p className="font-rajdhani text-[#F59E0B] text-sm mb-4" data-testid="pending-count">{pendingCount} risultati in attesa di approvazione</p>}

      <div className="space-y-6">
        {Array.from({ length: numMatches }, (_, index) => index + 1).map((matchNumber) => {
          const existingRows = results.filter((r) => r.match_number === matchNumber);
          const unpublished = existingRows.some((r) => !r.published);

          return (
            <Panel key={matchNumber} className="p-5" testid={`match-group-${matchNumber}`}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="heading text-2xl text-white">Partita {matchNumber}</h3>
                {unpublished && existingRows[0] && (
                  <button
                    data-testid={`publish-match-${existingRows[0].match_id}`}
                    onClick={() => publishMatch(existingRows[0].match_id)}
                    className="btn-outline-gold clip-beveled-sm px-4 py-1.5 text-xs flex items-center gap-1"
                  >
                    <Send className="w-3.5 h-3.5" /> Pubblica Match
                  </button>
                )}
              </div>

              <div className="space-y-3">
                {visibleTeams.map((team) => {
                  const r = resultBySlot.get(`${matchNumber}|${team.id}`);

                  if (!r) {
                    const draft = getNewResult(matchNumber, team);
                    const roster = rosterPlayers[team.id] || [];
                    const lobbyName =
                      lobbies.find((l) => l.id === team.lobby_id)?.name ||
                      team.lobby_id;

                    return (
                      <div
                        key={team.id}
                        className="border border-dashed border-[var(--steel)]/40 clip-beveled-sm p-4"
                        data-testid={`missing-result-${matchNumber}-${team.id}`}
                      >
                        <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="heading text-xl text-white">
                              {team.name}{" "}
                              <span className="text-[var(--gold)] text-xs">
                                ({team.tag})
                              </span>
                            </span>
                            <span className="font-rajdhani text-[10px] uppercase tracking-wider px-2 py-0.5 clip-beveled-sm bg-[var(--steel)]/20 text-[var(--silver)]">
                              Risultato mancante
                            </span>
                            <span className="font-rajdhani text-[10px] text-[var(--silver)]">
                              Lobby: {lobbyName}
                            </span>
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">
                              Piazzamento
                            </label>
                            <input
                              data-testid={`new-placement-${matchNumber}-${team.id}`}
                              type="number"
                              min="1"
                              value={draft.placement}
                              onChange={(event) =>
                                setNewPlace(matchNumber, team, event.target.value)
                              }
                              className="w-20 ml-2 bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1 text-center text-white focus:border-[var(--gold)] focus:outline-none"
                            />

                            <div className="mt-3 space-y-1">
                              {roster.map((player) => (
                                <div
                                  key={player.id}
                                  className="flex items-center justify-between font-rajdhani text-sm"
                                >
                                  <span className="text-white">
                                    {player.activision_id ||
                                      player.discord_username ||
                                      player.id}
                                  </span>
                                  <input
                                    data-testid={`new-kill-${matchNumber}-${team.id}-${player.id}`}
                                    type="number"
                                    min="0"
                                    value={draft.player_kills[player.id] ?? 0}
                                    onChange={(event) =>
                                      setNewKill(
                                        matchNumber,
                                        team,
                                        player.id,
                                        event.target.value
                                      )
                                    }
                                    className="w-16 bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1 text-center text-white focus:border-[var(--gold)] focus:outline-none"
                                  />
                                </div>
                              ))}

                              {roster.length === 0 && (
                                <p className="font-rajdhani text-xs text-[var(--destructive)]">
                                  Roster non disponibile.
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-end sm:justify-end">
                            <button
                              data-testid={`create-result-${matchNumber}-${team.id}`}
                              onClick={() => createResult(matchNumber, team)}
                              disabled={roster.length === 0}
                              className="btn-gold clip-beveled-sm px-4 py-2 text-xs flex items-center gap-1 disabled:opacity-40"
                            >
                              <Plus className="w-3.5 h-3.5" /> Inserisci risultato
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  const e = getEdit(r);

                  return (
                    <div key={team.id} className="border border-[var(--steel)]/30 clip-beveled-sm p-4" data-testid={`result-${r.team_id}`}>
                      <div className="flex justify-between items-center flex-wrap gap-2 mb-3">
                        <div className="flex items-center gap-3">
                          <span className="heading text-xl text-white">{r.team_name} <span className="text-[var(--gold)] text-xs">({r.team_tag})</span></span>
                          <span className={`font-rajdhani text-[10px] uppercase tracking-wider px-2 py-0.5 clip-beveled-sm ${STATUS_STYLE[r.status]}`} data-testid={`status-${r.team_id}`}>{STATUS_LABEL[r.status]}</span>
                          <span className="font-rajdhani text-[10px] text-[var(--silver)]" data-testid={`result-context-${r.team_id}`}>
                            Lobby: {lobbies.find((l) => l.id === r.lobby_id)?.name || r.lobby_id} · Match ID: {r.match_id}
                          </span>
                        </div>
                        <div className="font-oswald text-right"><span className="text-2xl font-bold text-[var(--gold)]">{r.points}</span><span className="font-rajdhani text-[10px] text-[var(--silver)] uppercase ml-1">punti</span></div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Piazzamento</label>
                          <input data-testid={`edit-placement-${r.team_id}`} type="number" value={e.placement} onChange={(ev) => setPlace(r, ev.target.value)} className="w-20 ml-2 bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1 text-center text-white focus:border-[var(--gold)] focus:outline-none" />
                          <div className="mt-3 space-y-1">
                            {r.player_kills.map((p) => (
                              <div key={p.player_id} className="flex items-center justify-between font-rajdhani text-sm">
                                <span className="text-white">{p.name}</span>
                                <input data-testid={`edit-kill-${r.team_id}-${p.player_id}`} type="number" value={e.player_kills[p.player_id] ?? 0} onChange={(ev) => setKill(r, p.player_id, ev.target.value)} className="w-16 bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1 text-center text-white focus:border-[var(--gold)] focus:outline-none" />
                              </div>
                            ))}
                            <div className="flex justify-between font-rajdhani text-xs text-[var(--silver)] pt-1 border-t border-[var(--steel)]/20"><span>Kill Totali</span><span className="text-white font-oswald">{r.kills}</span></div>
                          </div>
                        </div>
                        <div>
                          <label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Prove risultato</label>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            {r.screenshots.length ? r.screenshots.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`screenshot-${r.team_id}-${i}`} className="flex items-center gap-1 text-xs font-rajdhani text-[#22d3ee] border border-[#22d3ee]/40 clip-beveled-sm px-2 py-1 hover:bg-[#22d3ee]/10"><ImageIcon className="w-3.5 h-3.5" /> Screen {i + 1}</a>
                            )) : <span className="font-rajdhani text-xs text-[var(--silver)]">Nessuno</span>}
                            {r.evidence_note && (
                              <div data-testid={`evidence-note-${r.team_id}`} className="w-full mt-2 border border-[var(--gold)]/40 px-3 py-2">
                                <span className="block font-rajdhani text-[10px] uppercase text-[var(--gold)]">Nota al posto dello Screen 2</span>
                                <span className="block font-rajdhani text-sm text-white mt-1 whitespace-pre-wrap">{r.evidence_note}</span>
                              </div>
                            )}
                          </div>
                          {r.submitted_by && <p className="font-rajdhani text-[10px] text-[var(--silver)] mt-2">Inviato da: {r.submitted_by}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button data-testid={`approve-${r.team_id}`} onClick={() => approve(r)} className="btn-gold clip-beveled-sm px-4 py-1.5 text-xs flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Approva</button>
                        <button data-testid={`modify-${r.team_id}`} onClick={() => saveEdit(r)} className="btn-outline-gold clip-beveled-sm px-4 py-1.5 text-xs flex items-center gap-1"><Save className="w-3.5 h-3.5" /> Salva Modifica</button>
                        <button data-testid={`reject-${r.team_id}`} onClick={() => reject(r)} className="clip-beveled-sm px-4 py-1.5 text-xs flex items-center gap-1 border border-[var(--destructive)]/50 text-[var(--destructive)] hover:bg-[var(--destructive)]/10"><X className="w-3.5 h-3.5" /> Invalida</button>
                      </div>
                    </div>
                  );
                })}

                {visibleTeams.length === 0 && (
                  <p className="font-rajdhani text-[var(--silver)] text-sm py-4 text-center">
                    Nessuna squadra assegnata alla lobby selezionata.
                  </p>
                )}
              </div>
            </Panel>
          );
        })}

        <Panel className="p-6 mt-6" testid="adjustments-panel">
          <StarDivider label="Adjustment amministrativi" className="mb-4" />
          <p className="font-rajdhani text-xs text-[var(--silver)] mb-4">Aggiungi o sottrai punti alla classifica Team. Gli adjustment non modificano kill o classifica MVP.</p>
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Squadra</label><select value={adjForm.team_id} onChange={(e) => setAdjForm({ ...adjForm, team_id: e.target.value })} data-testid="adj-team" className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white"><option value="">Seleziona…</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
            <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Valore (± punti)</label><input data-testid="adj-value" type="number" value={adjForm.value} onChange={(e) => setAdjForm({ ...adjForm, value: e.target.value })} placeholder="es. -10 o 5" className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white" /></div>
            <div><label className="font-rajdhani text-[10px] uppercase text-[var(--silver)]">Motivazione</label><input data-testid="adj-reason" value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} className="w-full mt-1 bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white" /></div>
            <button data-testid="adj-submit" onClick={addAdjustment} disabled={!adjForm.team_id || adjForm.value === ""} className="btn-gold clip-beveled px-5 py-2.5 flex items-center justify-center gap-1 disabled:opacity-40"><Plus className="w-4 h-4" /> Applica</button>
          </div>
          <div className="mt-5 space-y-2">
            {adjustments.map((a) => <div key={a.id} className="flex justify-between items-center border-b border-[var(--steel)]/20 py-2 font-rajdhani text-sm" data-testid={`adj-row-${a.id}`}><div className="flex items-center gap-3"><span className={`font-oswald font-bold ${a.value < 0 ? "text-[var(--destructive)]" : "text-[#10B981]"}`}>{a.value > 0 ? "+" : ""}{a.value}</span><span className="text-white">{a.team_name}</span><span className="text-[var(--silver)] text-xs">{a.reason}</span></div><button data-testid={`adj-delete-${a.id}`} onClick={() => removeAdjustment(a.id)} className="text-[var(--destructive)]"><Trash2 className="w-4 h-4" /></button></div>)}
            {adjustments.length === 0 && <p className="font-rajdhani text-xs text-[var(--silver)]">Nessun adjustment applicato.</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
